<script setup>
// X216 (COUNTRY_PLAN.md §6.1): THE country picker for a club, used by the create wizard, club settings, the super club
// form, the sign-in question, Needs you and the Stripe confirm step. A searchable TlAsyncSelect (X199: a skeleton,
// never a raw id, while the list loads; `known` names the saved country at once). It NEVER defaults to the first
// country in the list: with `suggest` and nothing chosen it asks the API's countries/suggested (the account's country,
// then the device's timezone, then its language) and stays EMPTY when there is no answer.
import {computed, onMounted, watch} from 'vue';
import TlAsyncSelect from '~/components/TlAsyncSelect.vue';
import {useCountryList} from '~/composables/useCountryList';
import {useHttpRequest} from '~/composables/useHttpRequest';
import {endpoints} from '~/network/endpoints.js';
import {countryFilter, withDisplayName} from '~/utils/countryNames';

const model = defineModel({type: String, default: null});
const props = defineProps({
  /** The country already held for the chosen id ({id, name, isoCode}), so its name shows before the list loads. */
  known: {type: Object, default: null},
  errors: {type: [Array, String], default: null},
  /** Pre-select the API's suggestion when nothing is chosen yet (new clubs). */
  suggest: {type: Boolean, default: false},
  placeholder: {type: String, default: 'Choose country'},
  disabled: {type: Boolean, default: false},
  autofocus: {type: Boolean, default: false},
  density: {type: String, default: 'comfortable'},
});
const emit = defineEmits(['country']);

const {countries, pending, load, find} = useCountryList();
const {httpRequest} = useHttpRequest();

// X221 (R90 L-3): the natural name ("Isle of Man"), found by any of its names when typed.
const known = computed(() => withDisplayName(props.known));
const knownItems = computed(() => (known.value?.id ? [known.value] : []));
const selected = computed(() => (model.value ? find(model.value) ?? (known.value?.id === model.value ? known.value : null) : null));
watch(selected, (c) => emit('country', c ?? null));

onMounted(async () => {
  await load();
  if (!props.suggest || model.value) return;
  try {
    const locale = typeof navigator !== 'undefined' ? navigator.language : null;
    const s = await httpRequest(endpoints.countries.suggested(locale));
    if (!model.value && s?.countryId) model.value = s.countryId;
  } catch {
    // 204 (no suggestion) or offline: the field stays empty and Save stays off until one is chosen.
  }
});
</script>

<template>
  <tl-async-select
      searchable
      v-model="model"
      :items="countries"
      :known="knownItems"
      :loading="pending"
      item-value="id"
      item-title="name"
      autocomplete="off"
      variant="outlined"
      :density="density"
      hide-details="auto"
      :placeholder="placeholder"
      :custom-filter="countryFilter"
      :disabled="disabled"
      :autofocus="autofocus"
      :error="errors != null && errors.length > 0"
      :error-messages="errors"
  />
</template>
